<script lang="ts">
import { tv } from 'tailwind-variants';

const fileIcon = tv({
	slots: {
		base: 'aspect-square',
		outline: 'stroke-1 text-dimmed',
	},
} as const);

// Badge geometry in viewBox units. The box width scales with a rough character
// count up to BADGE_MAX_CHARS; longer labels keep the max-width box and compress.
const BADGE_MAX_CHARS = 5;
const BADGE_CHAR_ADVANCE = 3;
const BADGE_PADDING_X = 1.5;
const BADGE_X = 1;
</script>

<script setup lang="ts">
import type { FilePreviewData } from '#layers/assets/types/file';
import { computed } from 'vue';
import { mediaTypeColor, mediaTypeExtension } from '#layers/assets/utils/media-type';
import { getColorInfo } from '#layers/ui/public';

export type FileTypeIconFileInput = Partial<FilePreviewData>;

const { file, class: className } = defineProps<{
	file: FileTypeIconFileInput | File;
	class?: any;
}>();

const canonicalInfo = computed(() => {
	if (file instanceof File) {
		return file;
	}

	return {
		type: file.mediaType ?? undefined,
		name: file.fileName ?? undefined,
	};
});

const mediaType = computed(() => {
	if (canonicalInfo.value.type) {
		const extension = mediaTypeExtension(canonicalInfo.value.type);
		if (extension) return extension;
	}

	return canonicalInfo.value.name?.split('.').pop();
});

const color = computed(() => canonicalInfo.value.type ? mediaTypeColor(canonicalInfo.value.type) ?? undefined : undefined);

const label = computed(() => mediaType.value?.toUpperCase());

const badge = computed(() => {
	if (!label.value) return;

	if (color.value) {
		const { isLight } = getColorInfo(color.value);

		return {
			background: color.value,
			text: isLight
				? 'light-dark(var(--ui-text), var(--ui-text-inverted))'
				: 'light-dark(var(--ui-text-inverted), var(--ui-text))',
		};
	}

	return {
		background: 'var(--ui-bg-inverted)',
		text: 'var(--ui-text-inverted)',
	};
});

const badgeBox = computed(() => {
	const length = label.value?.length ?? 0;
	const innerWidth = Math.min(length, BADGE_MAX_CHARS) * BADGE_CHAR_ADVANCE;
	const width = innerWidth + BADGE_PADDING_X * 2;

	return {
		x: BADGE_X,
		width,
		centerX: BADGE_X + width / 2,
		// Compress only labels wider than the max-width box; shorter ones keep natural spacing.
		textLength: length > BADGE_MAX_CHARS ? innerWidth : undefined,
	};
});

const ui = computed(() => fileIcon());
</script>

<template>
	<svg
		:class="ui.base({ class: className })"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<g
			:class="ui.outline()"
			stroke="currentColor"
			stroke-linecap="round"
			stroke-linejoin="round"
		>
			<path
				d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"
				fill="var(--ui-bg)"
			/>
			<path d="M14 2v5a1 1 0 0 0 1 1h5" />
		</g>

		<template v-if="badge">
			<rect
				:x="badgeBox.x"
				y="11"
				:width="badgeBox.width"
				height="7"
				rx="1.5"
				:fill="badge.background"
			/>
			<text
				:x="badgeBox.centerX"
				y="16.2"
				text-anchor="middle"
				font-size="5"
				font-weight="700"
				:fill="badge.text"
				:textLength="badgeBox.textLength"
				length-adjust="spacingAndGlyphs"
			>{{ label }}</text>
		</template>
	</svg>
</template>
